import React, {ComponentProps, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {MultiValue} from "react-select";
import {useFormState} from "../../../hooks/useFormState";
import {getLanguages} from "../../../i18n";
import {Challenge, ChallengeFilterInput, Competence} from "../../../schema";
import {HelpCircleIcon} from "../../../ui/icons";
import Input from "../../../ui/input";
import Select from "../../../ui/select";
import WithTooltip from "../../../ui/tooltip";
import CompetenceSelect, {CompetenceOption} from "./CompetenceSelect";

import cn from "classnames";
import classes from "./ChallengesFilterForm.module.css";

type props = Omit<ComponentProps<"form">, "children" | "onSubmit"> & {
  initialValues?: ChallengeFilterInput;
  onSave?: (data: ChallengeFilterInput) => void;
  competencies?: Competence[];
};

type formState = {
  challengeName?: Challenge["name"];
  languageCode: Challenge["languageCode"] | "0";
};

export default function ChallengesFilterForm({initialValues, onSave, competencies = [], className, ...props}: props) {
  const {t} = useTranslation();

  const formState = useFormState<formState>({
    initialValues: {
      challengeName: initialValues?.name ?? undefined,
      languageCode: initialValues?.languageCode ?? "0",
    },
    preventDefault: true,
    onChange: (data) =>
      onSave &&
      onSave({
        name: data.challengeName,
        languageCode: data.languageCode === "0" ? undefined : data.languageCode,
      }),
  });

  const competenceOptions = useMemo<CompetenceOption[]>(
    () => competencies.map((c) => ({value: c.id, label: c.name})),
    [competencies],
  );

  const selectedCompetenceOptions = useMemo<CompetenceOption[]>(() => {
    const ids = initialValues?.competenceIds ?? [];
    return competenceOptions.filter((opt) => ids.includes(opt.value));
  }, [initialValues?.competenceIds, competenceOptions]);

  const handleCompetenceChange = (selected: MultiValue<CompetenceOption>) => {
    if (!onSave) return;
    const ids = selected.map((opt) => opt.value);
    onSave({
      name: formState.values.challengeName,
      languageCode: formState.values.languageCode === "0" ? undefined : formState.values.languageCode,
      competenceIds: ids.length > 0 ? ids : undefined,
    });
  };
  return (
    <form method="post" className={cn(classes.root, className)} onSubmit={formState.submitHandler} {...props}>
      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.ChallengesFilterForm.name.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengesFilterForm.name.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip className={classes.help} as="span" helpText={t("components.ChallengesFilterForm.name.helpText")}>
          <Input
            name="challengeName"
            displaySize="sm"
            placeholder={t("components.ChallengesFilterForm.name.placeholder")}
            value={formState.values.challengeName ?? undefined}
            onChange={formState.changeHandler}
          />
        </WithTooltip>
      </div>
      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.ChallengesFilterForm.language.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengesFilterForm.language.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip
          className={classes.help}
          as="span"
          helpText={t("components.ChallengesFilterForm.language.helpText")}
        >
          <Select
            name="languageCode"
            value={formState.values.languageCode ?? undefined}
            onChange={formState.changeHandler}
          >
            <option value={0}>{t("components.ChallengesFilterForm.language.no")}</option>
            {getLanguages().map((lang) => (
              <option key={lang.lang} value={lang.languageCode}>
                {lang.verboseName}
              </option>
            ))}
          </Select>
        </WithTooltip>
      </div>
      {competencies.length > 0 && (
        <div className={classes.formItem}>
          <label className={classes.label}>
            {t("components.ChallengesFilterForm.competencies.label")}
          </label>
          <CompetenceSelect
            options={competenceOptions}
            value={selectedCompetenceOptions}
            onChange={handleCompetenceChange}
            placeholder={t("components.ChallengesFilterForm.competencies.placeholder")}
            noOptionsMessage={() => t("components.ChallengesFilterForm.competencies.noOptions")}
          />
        </div>
      )}
    </form>
  );
}
